import { useState } from "react";

//* External
import { Card, Button, Form, Spinner, InputGroup } from "react-bootstrap";
import { FaTrashAlt, FaPen, FaArrowLeft, FaListUl, FaPlus, FaCheck, FaSearch } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";

//* Data hook + API + types
import { useQuestionLists } from "../../../hooks/questionLists/useQuestionLists";
import { questionListsAPI } from "../../../hooks/questionLists/questionListsAPI";
import { questionAPI } from "../../../hooks/questions/questionsAPI";
import { useDebounce } from "../../../hooks/useDebounce";
import { type QuestionListDetail } from "../../../hooks/questionLists/types/types";

export function MyListsView() {
	const token = localStorage.getItem("token") || "";

	const { lists, loading, renameList, deleteList, addItem, removeItem } =
		useQuestionLists(token);

	const [editingId, setEditingId] = useState<string | null>(null);
	const [editName, setEditName] = useState("");

	const [openList, setOpenList] = useState<QuestionListDetail | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [removingQuestionId, setRemovingQuestionId] = useState<string | null>(null);
	const [addingQuestionId, setAddingQuestionId] = useState<string | null>(null);

	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");

	const applySearch = useDebounce((value: string) => setSearch(value), 400);

	const existingIds = new Set(openList?.questions.map((q) => q.id) ?? []);

	const { data: searchData, isFetching: searchLoading } = useQuery({
		queryKey: ["my-lists-search", search],
		queryFn: () => questionAPI.getAll(search, 1, token, 10),
		enabled: !!openList && search.trim().length > 0,
		staleTime: 30_000,
	});

	const searchResults = searchData?.questions ?? [];

	const openListDetail = async (listId: string) => {
		setLoadingDetail(true);
		setSearchInput("");
		setSearch("");
		try {
			const detail = await questionListsAPI.getById(listId, token);
			setOpenList(detail);
		} catch (error) {
			console.error("Erro ao carregar a lista:", error);
		} finally {
			setLoadingDetail(false);
		}
	};

	const refreshOpenList = async () => {
		if (!openList) return;
		try {
			const detail = await questionListsAPI.getById(openList.id, token);
			setOpenList(detail);
		} catch (error) {
			console.error("Erro ao recarregar a lista:", error);
		}
	};

	const handleRename = async (id: string) => {
		const name = editName.trim();
		if (!name) return;
		try {
			await renameList({ id, name });
			setEditingId(null);
		} catch (error) {
			console.error("Erro ao renomear a lista:", error);
		}
	};

	const handleDelete = async (id: string) => {
		try {
			await deleteList(id);
		} catch (error) {
			console.error("Erro ao excluir a lista:", error);
		}
	};

	const handleRemoveQuestion = async (questionId: string) => {
		if (!openList) return;
		setRemovingQuestionId(questionId);
		try {
			await removeItem({ listId: openList.id, questionId });
			await refreshOpenList();
		} catch (error) {
			console.error("Erro ao remover a questão da lista:", error);
		} finally {
			setRemovingQuestionId(null);
		}
	};

	const handleAddQuestion = async (questionId: string) => {
		if (!openList) return;
		setAddingQuestionId(questionId);
		try {
			await addItem({ listId: openList.id, questionId });
			await refreshOpenList();
		} catch (error) {
			console.error("Erro ao adicionar a questão à lista:", error);
		} finally {
			setAddingQuestionId(null);
		}
	};

	return (
		<div className="col-12">
			<div className="py-5 px-3">
				<Card>
					<Card.Header className="d-flex align-items-center">
						{openList ? (
							<Button
								id="btn-back-to-lists"
								type="button"
								variant="link"
								className="p-0 me-2 text-decoration-none"
								aria-label="Voltar para as listas"
								onClick={() => setOpenList(null)}
							>
								<FaArrowLeft />
							</Button>
						) : (
							<FaListUl className="me-2" />
						)}
						<strong>{openList ? openList.name : "Minha Lista"}</strong>
					</Card.Header>

					<Card.Body className="d-flex flex-column gap-3">
						{openList ? (
							loadingDetail ? (
								<div className="d-flex justify-content-center py-3">
									<Spinner animation="border" role="status" />
								</div>
							) : (
								<>
									{/* Questões já na lista */}
									{openList.questions.length === 0 ? (
										<p className="text-muted mb-0">Esta lista está vazia.</p>
									) : (
										<div className="d-flex flex-column gap-2">
											{openList.questions.map((question) => (
												<div
													key={question.id}
													className="d-flex justify-content-between align-items-center border rounded p-2"
												>
													<div className="d-flex flex-column">
														<strong>{question.displayLabel ?? "Questão"}</strong>
														<small className="text-muted">
															{question.discipline?.name ?? "-"}
														</small>
													</div>
													<Button
														id={`btn-remove-list-question-${question.id}`}
														type="button"
														variant="outline-danger"
														size="sm"
														aria-label="Remover questão da lista"
														disabled={removingQuestionId === question.id}
														onClick={() => handleRemoveQuestion(question.id)}
													>
														<FaTrashAlt />
													</Button>
												</div>
											))}
										</div>
									)}

									{/* Buscar e adicionar questões */}
									<div className="border-top pt-3">
										<p className="mb-2 fw-semibold small text-muted">
											Adicionar questões à lista
										</p>
										<InputGroup className="mb-2">
											<InputGroup.Text>
												<FaSearch />
											</InputGroup.Text>
											<Form.Control
												id="input-search-questions-to-add"
												type="text"
												placeholder="Pesquisar questão pelo enunciado..."
												value={searchInput}
												onChange={(e) => {
													setSearchInput(e.target.value);
													applySearch(e.target.value);
												}}
											/>
										</InputGroup>

										{searchLoading && (
											<div className="d-flex justify-content-center py-2">
												<Spinner animation="border" size="sm" />
											</div>
										)}

										{!searchLoading && search.trim().length > 0 && searchResults.length === 0 && (
											<p className="text-muted small mb-0">Nenhuma questão encontrada.</p>
										)}

										{!searchLoading && searchResults.length > 0 && (
											<div className="d-flex flex-column gap-2">
												{searchResults.map((question) => {
													const alreadyIn = existingIds.has(question.id);
													return (
														<div
															key={question.id}
															className="d-flex justify-content-between align-items-center border rounded p-2"
														>
															<div className="d-flex flex-column">
																<strong>{question.displayLabel ?? "Questão"}</strong>
																<small className="text-muted">
																	{question.discipline?.name ?? "-"}
																	{question.year ? ` · ${question.year}` : ""}
																</small>
															</div>
															{alreadyIn ? (
																<Button
																	id={`btn-already-in-list-${question.id}`}
																	type="button"
																	variant="success"
																	size="sm"
																	disabled
																>
																	<FaCheck className="me-1" /> Na lista
																</Button>
															) : (
																<Button
																	id={`btn-add-to-list-${question.id}`}
																	type="button"
																	variant="outline-primary"
																	size="sm"
																	aria-label="Adicionar à lista"
																	disabled={addingQuestionId === question.id}
																	onClick={() => handleAddQuestion(question.id)}
																>
																	<FaPlus className="me-1" /> Adicionar
																</Button>
															)}
														</div>
													);
												})}
											</div>
										)}
									</div>
								</>
							)
						) : loading ? (
							<div className="d-flex justify-content-center py-3">
								<Spinner animation="border" role="status" />
							</div>
						) : lists.length === 0 ? (
							<p className="text-muted mb-0">Você ainda não tem listas.</p>
						) : (
							lists.map((list) => (
								<div
									key={list.id}
									className="d-flex justify-content-between align-items-center border rounded p-2 gap-2"
								>
									{editingId === list.id ? (
										<Form.Control
											type="text"
											value={editName}
											autoFocus
											onChange={(e) => setEditName(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													handleRename(list.id);
												}
											}}
										/>
									) : (
										<button
											id={`btn-open-list-${list.id}`}
											type="button"
											className="btn btn-link p-0 text-decoration-none text-start flex-grow-1"
											onClick={() => openListDetail(list.id)}
										>
											{list.name}{" "}
											<small className="text-muted">({list.itemsCount})</small>
										</button>
									)}

									<div className="d-flex gap-1">
										{editingId === list.id ? (
											<Button
												id={`btn-save-rename-${list.id}`}
												type="button"
												variant="primary"
												size="sm"
												onClick={() => handleRename(list.id)}
											>
												Salvar
											</Button>
										) : (
											<Button
												id={`btn-rename-list-${list.id}`}
												type="button"
												variant="outline-secondary"
												size="sm"
												aria-label="Renomear lista"
												onClick={() => {
													setEditingId(list.id);
													setEditName(list.name);
												}}
											>
												<FaPen />
											</Button>
										)}
										<Button
											id={`btn-delete-list-${list.id}`}
											type="button"
											variant="outline-danger"
											size="sm"
											aria-label="Excluir lista"
											onClick={() => handleDelete(list.id)}
										>
											<FaTrashAlt />
										</Button>
									</div>
								</div>
							))
						)}
					</Card.Body>
				</Card>
			</div>
		</div>
	);
}
